import { Session } from 'next-auth';

import { JourneyType, ProductModel } from '@bytel/leviator';
import { roundPrices } from '@bytel/product-wall/helpers';

import { cmsService } from '@services/cms';
import { getAppConfig } from '@services/config';

import { mCubeRepository } from '@repositories/mcube';
import { sapiRepository } from '@repositories/sapi';
import { SapicGetOptionsType, sapicRepository } from '@repositories/sapic';

import { SapiPlanType, SapiResponseType } from '@app-types/api/sapi';
import { SapicCategoryId, SapicFundingMode } from '@app-types/api/sapic';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { DiscountType } from '@app-types/discount';
import { MobilePlanDetailWithProvenanceType, MobilePlanProvenanceType, MobilePlanType } from '@app-types/plan';
import { SimulatorCart, VirtualCartQuote } from '@app-types/simulator/virtual-cart';
import { UserType } from '@app-types/user';

import { MarketingsState } from '@hooks/use-marketings';

import { mapApiPlanToMobilePlanType } from '@helpers/product';
import { isPlanCompatibleWithWall } from '@helpers/walls/plans';

import { MarketingType, PlanSubTypeId, PlanTypeId } from '@constants/plan';
import { TRADE_IN_GENCODE } from '@constants/trade-in';

const appConfig = getAppConfig();

export function getDefaultPlan(
    isRenewal: boolean,
    plans: { subType: PlanSubTypeId; gencode: string }[],
    isSmartChange: boolean,
): number {
    const subType = isSmartChange ? PlanSubTypeId.SMART_CHANGE : PlanSubTypeId.CLASSIC;
    const configPlans = isSmartChange ? appConfig.defaultPlans.smartChange : appConfig.defaultPlans.classic;

    if (!isRenewal) {
        return configPlans.acquisition;
    }

    const matchingPlan = plans.find(
        (plan) => plan.subType === subType && configPlans.renewal.includes(Number(plan.gencode)),
    );

    if (matchingPlan) {
        return Number(matchingPlan.gencode);
    }

    const firstPlanOfType = plans.find((plan) => plan.subType === subType);
    if (firstPlanOfType) {
        return Number(firstPlanOfType.gencode);
    }

    return configPlans.renewal[0]!;
}

async function handleExpectedSubType(
    mobilePlanDetailWithProvenance: MobilePlanDetailWithProvenanceType,
    plans: { subType: PlanSubTypeId; gencode: string }[],
    isSmartChange: boolean,
    isRenewal: boolean,
): Promise<MobilePlanDetailWithProvenanceType> {
    const expectedSubType = isSmartChange ? PlanSubTypeId.SMART_CHANGE : PlanSubTypeId.CLASSIC;

    if (mobilePlanDetailWithProvenance.plan && mobilePlanDetailWithProvenance.plan.subTypeId !== expectedSubType) {
        return {
            plan: await getPlanDetail(getDefaultPlan(isRenewal, plans, isSmartChange)),
            origin: MobilePlanProvenanceType.DEFAULT,
        };
    }
    return mobilePlanDetailWithProvenance;
}

export async function getWallCurrentPlan(
    type: WallType,
    searchParams?: URLSearchParams,
    planInCart?: ProductModel,
    isRenewal: boolean = false,
    contract?: ContractType,
    user?: UserType,
    isSmartChange: boolean = false,
): Promise<MobilePlanDetailWithProvenanceType> {
    const planInSearchParams = searchParams ? searchParams.get('forfait') : null;

    let renewalPlans: { subType: PlanSubTypeId; gencode: string }[] = [];

    if (isRenewal && contract && user) {
        renewalPlans =
            (await mCubeRepository.getSimulationImpactsOffres(user, contract.id))?.simulations.map((offer) => ({
                subType: offer.subType ?? PlanSubTypeId.CLASSIC,
                gencode: offer.offerId,
            })) ?? [];
    }

    try {
        const ownedPlan =
            isRenewal && contract !== undefined ? await getPlanDetail(contract.subscription.offerId) : undefined;

        if (ownedPlan?.isExcluPro) {
            return handleExpectedSubType(
                {
                    plan: ownedPlan,
                    origin: MobilePlanProvenanceType.OWNED,
                },
                renewalPlans,
                isSmartChange,
                isRenewal,
            );
        }

        if (!isRenewal && planInSearchParams === 'sans') {
            return {
                plan: null,
                origin: MobilePlanProvenanceType.URL,
            };
        }

        if (planInSearchParams !== null) {
            const planDetail = await getPlanDetail(planInSearchParams);
            if (isPlanCompatibleWithWall(type, planDetail.typeId)) {
                return handleExpectedSubType(
                    {
                        plan: planDetail,
                        origin: MobilePlanProvenanceType.URL,
                    },
                    renewalPlans,
                    isSmartChange,
                    isRenewal,
                );
            }
        }

        if (planInCart !== undefined) {
            if (isPlanCompatibleWithWall(type, planInCart.metadata!.type as PlanTypeId)) {
                return handleExpectedSubType(
                    {
                        plan: await getPlanDetail(planInCart.gencode),
                        origin: MobilePlanProvenanceType.CART,
                    },
                    renewalPlans,
                    isSmartChange,
                    isRenewal,
                );
            }
        }

        if (ownedPlan !== undefined) {
            if (isPlanCompatibleWithWall(type, ownedPlan.typeId)) {
                return handleExpectedSubType(
                    {
                        plan: ownedPlan,
                        origin: MobilePlanProvenanceType.OWNED,
                    },
                    renewalPlans,
                    isSmartChange,
                    isRenewal,
                );
            }
        }

        return {
            plan: await getPlanDetail(getDefaultPlan(isRenewal, renewalPlans, isSmartChange)),
            origin: MobilePlanProvenanceType.DEFAULT,
        };
    } catch {
        return {
            plan: await getPlanDetail(getDefaultPlan(isRenewal, renewalPlans, isSmartChange)),
            origin: MobilePlanProvenanceType.DEFAULT,
        };
    }
}

export async function getHotspotCurrentPlan(
    searchParams?: URLSearchParams,
    planInCart?: ProductModel,
): Promise<MobilePlanDetailWithProvenanceType> {
    const planInSearchParams = searchParams ? searchParams.get('forfait') : null;

    try {
        if (planInSearchParams === 'sans') {
            return {
                plan: null,
                origin: MobilePlanProvenanceType.URL,
            };
        }

        if (planInSearchParams !== null) {
            const planDetail = await getPlanDetail(planInSearchParams);
            if (isPlanCompatibleWithWall(WallType.HOTSPOT, planDetail.typeId)) {
                return {
                    plan: planDetail,
                    origin: MobilePlanProvenanceType.URL,
                };
            }
        }

        if (planInCart !== undefined) {
            if (isPlanCompatibleWithWall(WallType.HOTSPOT, planInCart.metadata!.type as PlanTypeId)) {
                return {
                    plan: await getPlanDetail(planInCart.gencode),
                    origin: MobilePlanProvenanceType.CART,
                };
            }
        }

        return {
            plan: await getPlanDetail(appConfig.defaultPlans.hotspot),
            origin: MobilePlanProvenanceType.DEFAULT,
        };
    } catch {
        return {
            plan: await getPlanDetail(appConfig.defaultPlans.hotspot),
            origin: MobilePlanProvenanceType.DEFAULT,
        };
    }
}

export async function getPlanDetail(key: string | number): Promise<MobilePlanType> {
    const data = await sapiRepository.getProductByGencode<SapiPlanType>(key.toString());
    return mapApiPlanToMobilePlanType(data);
}

export const filterPromotionsByTypesMarketing = (
    promos: DiscountType[],
    types: MarketingType[],
    withIncitation: boolean = false,
    shouldInclude: boolean = true,
): DiscountType[] => {
    if (!promos || promos.length <= 0) {
        return [];
    }

    return promos.filter((promo) => {
        const isMarketingTypeIncluded = types.some((type) => promo.marketingTypes.includes(type));
        const isValidIncitation = withIncitation || promo.isIncitation === withIncitation;
        return shouldInclude
            ? isMarketingTypeIncluded && isValidIncitation
            : !isMarketingTypeIncluded && isValidIncitation;
    });
};

const getPromotionsAmount = (promos: DiscountType[]): number => {
    if (promos.length <= 0) {
        return 0;
    }
    return promos.reduce((acc, promotion) => acc + promotion.value, 0);
};

export type PlanPrice = {
    base: number;
    final: number;
    forever: number;
    striked: number;
    promoDuration: number;
};

export const getPlanPrice = (
    plan: MobilePlanType,
    withTypeMarketing: MarketingsState,
    simulatedPortabilitySelected: boolean,
): PlanPrice => {
    const price = {
        base: plan.details.price.initial,
        final: plan.details.price.initial,
        forever: plan.details.price.initial,
        striked: plan.details.price.initial,
        duration: 0,
    };

    const promos = plan.details.discounts;

    const promosPorta = filterPromotionsByTypesMarketing(promos, [MarketingType.PORTABILITY], true, true);
    const promosHome = filterPromotionsByTypesMarketing(promos, [MarketingType.HOME], false, true);
    const otherPromos = filterPromotionsByTypesMarketing(
        promos,
        [MarketingType.PORTABILITY, MarketingType.HOME],
        true,
        false,
    );

    // striked = prix initial moins toutes les remises sauf HOME (aligné avec le mur des offres)
    price.striked -= getPromotionsAmount(otherPromos);

    if (simulatedPortabilitySelected) {
        price.final -= getPromotionsAmount(promosPorta);
        price.striked -= getPromotionsAmount(promosPorta);
    }

    if (withTypeMarketing[MarketingType.HOME]) {
        price.forever -= getPromotionsAmount(promosHome);
        price.final -= getPromotionsAmount(promosHome);
    }

    if (otherPromos && otherPromos.length > 0) {
        price.final -= getPromotionsAmount(otherPromos);
        price.base -= getPromotionsAmount(otherPromos);
        otherPromos.forEach((promo) => {
            if (promo.duration === 0) {
                price.forever -= getPromotionsAmount([promo]);
            }
        });
    }

    price.duration = Math.max(...promos.map((promo) => promo.duration));

    if (price.final.toFixed(2) === price.forever.toFixed(2)) {
        price.duration = 0;
    }

    return {
        base: roundPrices(price.base),
        final: roundPrices(price.final),
        forever: roundPrices(price.forever),
        striked: roundPrices(price.striked),
        promoDuration: price.duration,
    };
};

export function getPlanTypeFromUrlKey(urlKey?: string | null): PlanTypeId {
    switch (true) {
        case urlKey?.includes('smartphone'):
            return PlanTypeId.PREMIUM;
        case urlKey?.includes('sim-seule'):
            return PlanTypeId.SIMO;
        case urlKey?.includes('b-you'):
            return PlanTypeId.SOWO;
        case urlKey?.includes('faim'):
            return PlanTypeId.FAIM_PREMIUM;
        default:
            return PlanTypeId.PREMIUM;
    }
}

export const getVirtualCart = ({
    productGencode,
    isRenewal,
    contractId,
    clientBbox,
    shouldAddMobileJourney = true,
    includeTradeIn = false,
}: {
    productGencode?: string;
    isRenewal: boolean;
    contractId?: string;
    clientBbox?: boolean;
    shouldAddMobileJourney?: boolean;
    includeTradeIn?: boolean;
}): SimulatorCart => {
    const products: { gencode: string }[] = productGencode ? [{ gencode: productGencode }] : [];

    if (includeTradeIn && productGencode) {
        products.push({ gencode: TRADE_IN_GENCODE });
    }

    const virtualCart: SimulatorCart = {
        cart: {
            quotes: [
                {
                    type: isRenewal ? JourneyType.RENEWAL : JourneyType.MOBILE_ACQUISITION,
                    isCurrent: true,
                    products,
                    contractId,
                },
            ],
        },
    };

    const genericBboxGencode = appConfig.defaultPlans?.simulator?.fai;
    const genericPlanGencode = appConfig.defaultPlans?.simulator?.mobile;

    const addQuoteToCart = (type: JourneyType, gencode: string) => {
        virtualCart.cart.quotes.push({
            type,
            isCurrent: false,
            products: [{ gencode }],
            contractId,
        });
    };

    if (!isRenewal && clientBbox && genericBboxGencode && genericPlanGencode) {
        // we want to show the prices for an existing customer of bbox + mobile
        addQuoteToCart(JourneyType.FAI_ACQUISITION, genericBboxGencode);
    }

    if (genericPlanGencode && shouldAddMobileJourney) {
        addQuoteToCart(JourneyType.MOBILE_ACQUISITION, genericPlanGencode);
    }

    return virtualCart;
};

export const getMobilePlans = async ({
    productGencode,
    isRenewal,
    categorie,
    contractId,
    planGencodes = [],
    session,
    isClientBbox,
}: {
    productGencode?: string;
    isRenewal: boolean;
    categorie?: PlanTypeId;
    contractId?: string;
    planGencodes?: string[];
    session?: Session;
    isClientBbox?: boolean;
}): Promise<MobilePlanType[]> => {
    const virtualCart = getVirtualCart({
        productGencode,
        isRenewal,
        contractId,
        clientBbox: isClientBbox,
        shouldAddMobileJourney: false,
        includeTradeIn: true,
    });
    const category = categorie ? (categorie as unknown as SapicCategoryId) : undefined;
    const planParams: SapicGetOptionsType = {
        category,
        fundingMode: SapicFundingMode.MIN_ONE_OFF,
        withDetails: true,
    };

    let plans: MobilePlanType[] = [];
    if (!isRenewal && Boolean(category)) {
        plans = await sapicRepository
            .getMobilePlans(virtualCart, planParams, session)
            .then((response) => response.products);
    }

    const notIncludedPlanGencodes = planGencodes.filter((gencode) => {
        return !plans.some((plan) => plan.gencode === gencode);
    });

    if (notIncludedPlanGencodes.length > 0) {
        const planCartParams: SapicGetOptionsType = {
            gencodes: notIncludedPlanGencodes,
            fundingMode: SapicFundingMode.MIN_ONE_OFF,
            withDetails: true,
        };

        const cartPlan = await sapicRepository.getMobilePlans(virtualCart, planCartParams, session).then((response) => {
            return response.products;
        });

        plans = [...plans, ...cartPlan];
    }

    plans = plans.filter((p) => p.simulatedCart?.fundings.length);

    const cmsPlans = await cmsService.getPlansCms();
    return await transformToPlanTypeList(plans, categorie, productGencode, cmsPlans);
};

export function getQuotePayload(
    clientBbox?: boolean,
    phoneGencode?: string,
    isConnected?: boolean,
): VirtualCartQuote[] {
    if (isConnected) {
        const quote: VirtualCartQuote = {
            isCurrent: true,
            products: [] as { gencode: string }[],
            type: JourneyType.MOBILE_ACQUISITION,
        };

        if (phoneGencode) {
            quote?.products.push({
                gencode: phoneGencode,
            });
        }

        return [quote];
    }

    const appConfig = getAppConfig();

    const mobileQuote: VirtualCartQuote = {
        isCurrent: true,
        type: JourneyType.MOBILE_ACQUISITION,
        products: [],
    };

    const faiQuote: VirtualCartQuote = {
        isCurrent: false,
        type: JourneyType.FAI_ACQUISITION,
        products: [
            {
                gencode: `${appConfig.defaultPlans.simulator.fai}`,
            },
        ],
    };

    const quotePayload: VirtualCartQuote[] = [mobileQuote];

    if (clientBbox) {
        quotePayload.unshift(faiQuote);
    }

    if (phoneGencode) {
        quotePayload
            .find((quote) => quote.type === JourneyType.MOBILE_ACQUISITION && quote.products.length === 0)
            ?.products.push({
                gencode: phoneGencode,
            });
    }
    return quotePayload;
}

export const fetchSinglePlan = async (gencode: string, session: Session | null) => {
    const quotes = getQuotePayload(false);
    const sapicRes = await sapicRepository.getMobilePlans(
        { cart: { quotes } },
        { filterByGencodes: [gencode] },
        session,
    );
    if (sapicRes.products.length > 0) {
        const sapicPlan = sapicRes.products[0];

        const searchParams = new URLSearchParams({
            listGencode: gencode,
        });

        const sapiPlan = await sapiRepository.getProducts<SapiResponseType<SapiPlanType>>(`?${searchParams}`);

        if (sapiPlan.produits[gencode]) {
            return mapApiPlanToMobilePlanType(sapiPlan.produits[gencode], sapicPlan);
        }
    }
};

const transformToPlanTypeList = async (
    plans: MobilePlanType[],
    category?: PlanTypeId,
    gencode?: string,
    cmsPlans?: Awaited<ReturnType<typeof cmsService.getPlansCms>>,
): Promise<MobilePlanType[]> => {
    if (!plans || plans.length === 0) {
        return [];
    }

    const searchParams = new URLSearchParams({
        listGencode: plans.map((plan) => plan.gencode).join(','),
    });

    if (category) {
        searchParams.set('categorie', category);
    }

    const result = await sapiRepository.getProducts<SapiResponseType<SapiPlanType>>(`?${searchParams}`);

    const sortedPlans = Object.values(result.produits).sort((a, b) => a.data_size - b.data_size);

    return sortedPlans.map((product) => {
        const plan = plans.find((p) => p.gencode === product.gencode)!;
        const cmsPlan = cmsPlans?.items.find((planCms) => planCms.gencode === product.gencode);
        return mapApiPlanToMobilePlanType(product, plan, gencode, cmsPlan);
    });
};

function productDetailDefaultPlan(
    _plans: MobilePlanType[],
    isRenewal: boolean = false,
    isSmartChange: boolean = false,
) {
    const plans = _plans.map((plan) => ({ subType: plan.subTypeId, gencode: plan.gencode }));
    const defaultPlanGencode = getDefaultPlan(isRenewal, plans, isSmartChange).toString();
    const defaultPlanGencodeOtherType = getDefaultPlan(isRenewal, plans, !isSmartChange).toString();

    return (
        _plans.find((plan) => plan.gencode === defaultPlanGencode.toString()) ??
        _plans.find((plan) => plan.gencode === defaultPlanGencodeOtherType.toString()) ??
        _plans[0] ??
        null
    );
}

function handleProductDetailExpectedSubType(
    mobilePlan: MobilePlanType,
    plans: MobilePlanType[],
    isRenewal: boolean = false,
    isSmartChange: boolean = false,
) {
    const expectedSubType = isSmartChange ? PlanSubTypeId.SMART_CHANGE : PlanSubTypeId.CLASSIC;

    if (mobilePlan && mobilePlan.subTypeId !== expectedSubType) {
        return productDetailDefaultPlan(plans, isRenewal, isSmartChange);
    }

    return mobilePlan;
}

export const getTradeInBonusForPhoneOnly = async (parentPhoneGencode: string): Promise<number> => {
    const virtualCart: SimulatorCart = {
        cart: {
            quotes: [
                {
                    type: JourneyType.MOBILE_ACQUISITION,
                    isCurrent: true,
                    products: [{ gencode: TRADE_IN_GENCODE }],
                },
            ],
        },
    };

    const options: SapicGetOptionsType = {
        gencodes: [parentPhoneGencode],
        fundingMode: SapicFundingMode.MIN_ONE_OFF,
        withDetails: false,
    };

    const response = await sapicRepository.getProducts(virtualCart, options);
    const phone = response.products.find((p) => p.gencode === parentPhoneGencode);

    if (!phone) return 0;

    const bonusFromSimulation =
        phone.simulatedCart?.quotes
            ?.flatMap((quote) => quote.products)
            .find((p) => p.gencode === parentPhoneGencode)
            ?.details?.discounts.find((d) => d.marketingTypes.includes(MarketingType.TRADE_IN_BONUS))?.value ?? 0;

    if (bonusFromSimulation > 0) return bonusFromSimulation;

    return phone.details.discounts.find((d) => d.marketingTypes.includes(MarketingType.TRADE_IN_BONUS))?.value ?? 0;
};

export function getPhoneCurrentPlan(
    type: WallType,
    plans: MobilePlanType[],
    planInCartGencode?: string,
    isRenewal: boolean = false,
    isSmartChange: boolean = false,
    urlPlan?: string | null,
    contract?: ContractType,
): MobilePlanType | null {
    if (urlPlan === 'sans') {
        return null;
    }

    if (urlPlan !== null) {
        const plan = plans.find((plan) => plan.urlKey === urlPlan);
        if (plan && isPlanCompatibleWithWall(type, plan.typeId)) {
            return plan;
        }
    }

    if (planInCartGencode !== undefined) {
        const plan = plans.find((plan) => plan.gencode === planInCartGencode);
        if (plan && isPlanCompatibleWithWall(type, plan.typeId)) {
            return handleProductDetailExpectedSubType(plan, plans, isRenewal, isSmartChange);
        }
    }

    if (isRenewal && contract !== undefined) {
        const ownedPlan = plans.find((p) => p.gencode === contract.subscription.offerId);
        if (ownedPlan && isPlanCompatibleWithWall(type, ownedPlan.typeId)) {
            return handleProductDetailExpectedSubType(
                ownedPlan,
                plans,
                isRenewal,
                ownedPlan.subTypeId === PlanSubTypeId.SMART_CHANGE || isSmartChange,
            );
        }
    }

    if (type === WallType.HOTSPOT) {
        return plans.find((plan) => plan.gencode === appConfig.defaultPlans.hotspot) ?? plans[0] ?? null;
    }

    return productDetailDefaultPlan(plans, isRenewal, isSmartChange);
}
